import { backgroundOrange, blackOrange, darkOrange, gold, green, orange } from '@/theme/colors'
import styled from 'styled-components'

export const ReadOnlyHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 0.875rem 1rem;
    background: ${backgroundOrange};
    border-radius: 12px;
    margin-bottom: 1rem;
    opacity: 0.85;
`

export const ReadOnlyPerson = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.375rem;
`

export const ReadOnlyLabel = styled.span`
    font-size: 0.6875rem;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
`

export const ReadOnlyAvatar = styled.div`
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: #d1d5db;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 14px;
    flex-shrink: 0;
    overflow: hidden;
    filter: grayscale(0.5);

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        opacity: 0.85;
    }
`

export const ReadOnlyName = styled.span`
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
`

export const ReadOnlyArrow = styled.div`
    color: ${darkOrange};
    font-size: 1.25rem;
    font-weight: 700;
`

export const FormGroup = styled.div`
    margin-bottom: 1.25rem;

    &:last-child {
        margin-bottom: 0;
    }
`

export const FormLabel = styled.label`
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 8px;
`

export const AmountInputWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`

export const AmountPrefix = styled.span`
    position: absolute;
    left: 16px;
    font-size: 16px;
    color: #6b7280;
    pointer-events: none;
`

export const AmountInput = styled.input`
    width: 100%;
    padding: 12px 16px 12px 32px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 16px;
    transition: border-color 0.2s, box-shadow 0.2s;

    &:focus {
        outline: none;
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
    -moz-appearance: textfield;
`

export const ButtonGroup = styled.div`
    display: flex;
    gap: 0.5rem;
    width: 100%;
    justify-content: space-between;
`

export const ButtonGroupRight = styled.div`
    display: flex;
    gap: 0.5rem;
`

export const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
    padding: 0.625rem 1.25rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s;
    border: 1px solid transparent;

    background: ${({ $variant }) => {
        if ($variant === 'primary') return green
        if ($variant === 'danger') return '#dc2626'
        return 'white'
    }};
    color: ${({ $variant }) => {
        if ($variant === 'primary' || $variant === 'danger') return 'white'
        return '#374151'
    }};
    border-color: ${({ $variant }) => {
        if ($variant === 'primary') return green
        if ($variant === 'danger') return '#dc2626'
        return '#d1d5db'
    }};

    &:hover:not(:disabled) {
        opacity: 0.9;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`

export const SoftWarning = styled.div`
    font-size: 0.8125rem;
    color: ${gold};
    background: #fef3e2;
    padding: 0.5rem 0.75rem;
    border-radius: 6px;
    margin-top: 0.5rem;
`

export const CrossUserWarning = styled.div`
    font-size: 0.8125rem;
    color: #92400e;
    background: #fef3c7;
    border: 1px solid #fde68a;
    padding: 0.625rem 0.875rem;
    border-radius: 8px;
    margin-bottom: 1rem;
    line-height: 1.4;
`

export const ValidationError = styled.div`
    font-size: 0.8125rem;
    color: #dc2626;
    margin-top: 0.375rem;
`

export const FilesSection = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
`

export const FilePreview = styled.div`
    position: relative;
    width: 96px;
    height: 96px;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #e5e7eb;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        cursor: pointer;
    }
`

export const FileRemoveButton = styled.button`
    position: absolute;
    top: 4px;
    right: 4px;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.6);
    color: white;
    border: none;
    font-size: 0.75rem;
    line-height: 1;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background: rgba(0, 0, 0, 0.8);
    }
`

export const FileAddPlaceholder = styled.label`
    width: 96px;
    height: 96px;
    border-radius: 8px;
    border: 2px dashed #d1d5db;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #9ca3af;
    background: white;
    font-size: 1.5rem;

    &:hover {
        border-color: ${orange};
        color: ${orange};
    }
`

export const ImageOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.85);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    cursor: pointer;

    img {
        max-width: 90%;
        max-height: 90%;
        object-fit: contain;
    }
`

export const HiddenFileInput = styled.input`
    display: none;
`

export const SectionLabel = styled.span`
    display: block;
    font-size: 0.6875rem;
    font-weight: 600;
    color: ${blackOrange};
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 0.5rem;
`
